import {
  connectorRotationReasons,
  isConnectorMetadataPatchOutcome,
  isConnectorMetadataPatchRequest,
  isConnectorRotationRequest,
  isConnectorRotationRequestOutcome,
  type ConnectorDetailSnapshot,
  type ConnectorMetadataPatchOutcome,
  type ConnectorRotationReason,
  type IdempotencyKey,
} from "@whitehaven/contracts";
import { FormActions, FormField, Select, TextInput } from "@whitehaven/ui";
import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  captureProtectedStateGeneration,
  isProtectedStateGenerationCurrent,
  registerProtectedStateCleaner,
} from "../../protected-state";
import type { AdminConnectorSettingsGateways } from "./admin-connector-settings-gateway";

interface ConnectorConfigurationPanelProps {
  readonly detail: ConnectorDetailSnapshot;
  readonly gateways: AdminConnectorSettingsGateways;
  readonly canPatchMetadata: boolean;
  readonly canRequestRotation: boolean;
  readonly notice: string | null;
  readonly onMutationConfirmed: (message: string) => void;
  readonly onRefreshRequested: () => void;
}

interface MetadataDraft {
  readonly provider: string;
  readonly mode: string;
  readonly safeIdentifierAlias: string;
}

interface Feedback {
  readonly tone: "positive" | "critical";
  readonly message: string;
  readonly refreshRequired: boolean;
}

interface MutationAttempt {
  readonly fingerprint: string;
  readonly idempotencyKey: IdempotencyKey;
}

const rotationReasonLabels: Readonly<Record<ConnectorRotationReason, string>> =
  {
    SCHEDULED_ROTATION: "Scheduled rotation",
    SUSPECTED_COMPROMISE: "Suspected compromise",
    ACCESS_CHANGE: "Access change",
    PROVIDER_REQUIREMENT: "Provider requirement",
    RECOVERY: "Recovery",
  };

function draftFromDetail(detail: ConnectorDetailSnapshot): MetadataDraft {
  return {
    provider: detail.connector.provider,
    mode: detail.connector.mode,
    safeIdentifierAlias: detail.connector.safeIdentifierAlias ?? "",
  };
}

function nullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function createIdempotencyKey(): IdempotencyKey | null {
  try {
    const value = globalThis.crypto?.randomUUID?.();
    return value && /^[A-Za-z0-9_-]{8,128}$/.test(value)
      ? (value as IdempotencyKey)
      : null;
  } catch {
    return null;
  }
}

function idempotencyKeyFor(
  attempt: { current: MutationAttempt | null },
  fingerprint: string,
): IdempotencyKey | null {
  if (attempt.current?.fingerprint === fingerprint) {
    return attempt.current.idempotencyKey;
  }
  const idempotencyKey = createIdempotencyKey();
  if (idempotencyKey) {
    attempt.current = { fingerprint, idempotencyKey };
  }
  return idempotencyKey;
}

function isConfirmedPatchConsistent(
  outcome: ConnectorMetadataPatchOutcome,
  detail: ConnectorDetailSnapshot,
  request: Parameters<
    NonNullable<AdminConnectorSettingsGateways["patchConnector"]>
  >[1],
): boolean {
  if (outcome.kind !== "confirmed") return false;
  return (
    outcome.connectorId === detail.connector.connectorId &&
    outcome.environment === detail.environment &&
    outcome.previousVersion === detail.connector.configurationVersion &&
    outcome.provider === request.provider &&
    outcome.mode === request.mode &&
    outcome.safeIdentifierAlias === request.safeIdentifierAlias
  );
}

export function ConnectorConfigurationPanel({
  detail,
  gateways,
  canPatchMetadata,
  canRequestRotation,
  notice,
  onMutationConfirmed,
  onRefreshRequested,
}: ConnectorConfigurationPanelProps) {
  const { connector } = detail;
  const patchConnector = canPatchMetadata ? gateways.patchConnector : null;
  const requestRotation = canRequestRotation ? gateways.requestRotation : null;
  const [draft, setDraft] = useState<MetadataDraft>(() =>
    draftFromDetail(detail),
  );
  const eligibleRotationSecrets = connector.requiredSecrets.filter(
    ({ status }) => status === "REFERENCED" || status === "VERIFIED",
  );
  const [secretName, setSecretName] = useState(
    eligibleRotationSecrets[0]?.name ?? "",
  );
  const [rotationReason, setRotationReason] =
    useState<ConnectorRotationReason>("SCHEDULED_ROTATION");
  const [pending, setPending] = useState<"metadata" | "rotation" | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const activeMutation = useRef<AbortController | null>(null);
  const metadataAttempt = useRef<MutationAttempt | null>(null);
  const rotationAttempt = useRef<MutationAttempt | null>(null);
  const feedbackRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    const clear = () => {
      activeMutation.current?.abort();
      activeMutation.current = null;
      setPending(null);
      setFeedback(null);
      metadataAttempt.current = null;
      rotationAttempt.current = null;
      setDraft(draftFromDetail(detail));
      setSecretName(
        detail.connector.requiredSecrets.find(
          ({ status }) => status === "REFERENCED" || status === "VERIFIED",
        )?.name ?? "",
      );
      setRotationReason("SCHEDULED_ROTATION");
    };
    const unregister = registerProtectedStateCleaner(
      `admin-connector-configuration-${connector.connectorId}`,
      clear,
    );
    return () => {
      unregister();
      activeMutation.current?.abort();
      activeMutation.current = null;
    };
  }, [connector.connectorId, detail]);

  useEffect(() => {
    if (feedback) feedbackRef.current?.focus();
  }, [feedback]);

  const setDraftField = (field: keyof MetadataDraft, value: string) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const submitMetadata = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!patchConnector || activeMutation.current) return;

    const request = {
      expectedVersion: connector.configurationVersion ?? 0,
      provider: draft.provider.trim(),
      mode: draft.mode.trim(),
      safeIdentifierAlias: nullable(draft.safeIdentifierAlias),
    };
    const requestFingerprint = JSON.stringify(request);
    const idempotencyKey = idempotencyKeyFor(
      metadataAttempt,
      requestFingerprint,
    );
    if (!isConnectorMetadataPatchRequest(request) || !idempotencyKey) {
      setFeedback({
        tone: "critical",
        message:
          "The safe configuration change could not be prepared. Review the fields and try again.",
        refreshRequired: false,
      });
      return;
    }

    const controller = new AbortController();
    activeMutation.current = controller;
    const generation = captureProtectedStateGeneration();
    setPending("metadata");
    setFeedback(null);
    try {
      const outcome = await patchConnector(connector.connectorId, request, {
        signal: controller.signal,
        idempotencyKey,
      });
      if (
        controller.signal.aborted ||
        !isProtectedStateGenerationCurrent(generation)
      ) {
        return;
      }
      if (!isConnectorMetadataPatchOutcome(outcome)) {
        throw new Error("Invalid connector metadata response.");
      }
      if (outcome.kind === "conflict") {
        metadataAttempt.current = null;
        setFeedback({
          tone: "critical",
          message:
            outcome.code === "STALE_VERSION"
              ? "Connector settings changed elsewhere. Reload safe details before editing again."
              : "This change conflicts with an earlier request. Reload safe details before editing again.",
          refreshRequired: true,
        });
        return;
      }
      if (!isConfirmedPatchConsistent(outcome, detail, request)) {
        throw new Error("Inconsistent connector metadata response.");
      }
      metadataAttempt.current = null;
      onMutationConfirmed(
        outcome.replayed
          ? "The previously confirmed metadata change was found and safe details were refreshed."
          : "Safe connector metadata was saved as a new configuration version.",
      );
    } catch {
      if (
        !controller.signal.aborted &&
        isProtectedStateGenerationCurrent(generation)
      ) {
        setFeedback({
          tone: "critical",
          message:
            "The connector metadata change was not confirmed. No local success state was applied.",
          refreshRequired: false,
        });
      }
    } finally {
      if (activeMutation.current === controller) {
        activeMutation.current = null;
        setPending(null);
      }
    }
  };

  const submitRotation = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      !requestRotation ||
      activeMutation.current ||
      connector.configurationVersion === null
    ) {
      return;
    }

    const request = {
      secretName,
      reason: rotationReason,
      requestedVersion: connector.configurationVersion,
    };
    const requestFingerprint = JSON.stringify(request);
    const idempotencyKey = idempotencyKeyFor(
      rotationAttempt,
      requestFingerprint,
    );
    if (!isConnectorRotationRequest(request) || !idempotencyKey) {
      setFeedback({
        tone: "critical",
        message:
          "The rotation request could not be prepared. Reload safe details and try again.",
        refreshRequired: false,
      });
      return;
    }

    const controller = new AbortController();
    activeMutation.current = controller;
    const generation = captureProtectedStateGeneration();
    setPending("rotation");
    setFeedback(null);
    try {
      const outcome = await requestRotation(connector.connectorId, request, {
        signal: controller.signal,
        idempotencyKey,
      });
      if (
        controller.signal.aborted ||
        !isProtectedStateGenerationCurrent(generation)
      ) {
        return;
      }
      if (!isConnectorRotationRequestOutcome(outcome)) {
        throw new Error("Invalid connector rotation response.");
      }
      if (outcome.kind === "conflict") {
        rotationAttempt.current = null;
        setFeedback({
          tone: "critical",
          message:
            outcome.code === "STALE_VERSION"
              ? "Connector settings changed before the request was recorded. Reload safe details and try again."
              : "This rotation request conflicts with an earlier request. Reload safe details before trying again.",
          refreshRequired: true,
        });
        return;
      }
      if (
        outcome.connectorId !== connector.connectorId ||
        outcome.environment !== detail.environment ||
        outcome.secretName !== request.secretName ||
        outcome.reason !== request.reason ||
        outcome.requestedVersion !== request.requestedVersion
      ) {
        throw new Error("Inconsistent connector rotation response.");
      }
      rotationAttempt.current = null;
      onMutationConfirmed(
        outcome.replayed
          ? "The existing rotation request was found and safe event details were refreshed."
          : `Rotation request recorded. It expires at ${outcome.expiresAt}.`,
      );
    } catch {
      if (
        !controller.signal.aborted &&
        isProtectedStateGenerationCurrent(generation)
      ) {
        setFeedback({
          tone: "critical",
          message:
            "The rotation request was not confirmed. No credential value was requested or transmitted.",
          refreshRequired: false,
        });
      }
    } finally {
      if (activeMutation.current === controller) {
        activeMutation.current = null;
        setPending(null);
      }
    }
  };

  const refreshRequired = feedback?.refreshRequired === true;

  return (
    <section
      className="admin-settings__configuration"
      aria-labelledby="safe-configuration-title"
    >
      <div className="admin-settings__configuration-heading">
        <div>
          <p className="portal-kicker">Protected commands</p>
          <h3 id="safe-configuration-title">Safe configuration</h3>
        </div>
        <span>Version {connector.configurationVersion ?? "not created"}</span>
      </div>
      <p>
        Only non-secret metadata crosses this form. Credential values remain in
        approved provider consoles or encrypted server-side secret stores.
        Saving also requires recent server-verified multi-factor authentication.
      </p>

      {notice && (
        <p
          className="admin-settings__feedback admin-settings__feedback--positive"
          role="status"
        >
          {notice}
        </p>
      )}
      {feedback && (
        <p
          ref={feedbackRef}
          className={`admin-settings__feedback admin-settings__feedback--${feedback.tone}`}
          role={feedback.tone === "critical" ? "alert" : "status"}
          tabIndex={-1}
        >
          {feedback.message}
        </p>
      )}
      {feedback?.refreshRequired && (
        <button type="button" onClick={onRefreshRequested}>
          Reload safe details
        </button>
      )}
      {patchConnector === null && (
        <p className="admin-settings__unavailable">
          {canPatchMetadata
            ? "Safe metadata transport is not configured."
            : "This account does not have permission to change connector metadata."}
        </p>
      )}

      <form onSubmit={submitMetadata}>
        <div className="admin-settings__form-grid">
          <FormField
            label="Provider"
            hint="Use the approved provider code for this connector."
            required
          >
            {(controlProps) => (
              <TextInput
                {...controlProps}
                value={draft.provider}
                pattern="[a-z][a-z0-9._+\-]{0,127}"
                maxLength={128}
                autoComplete="off"
                disabled={
                  pending !== null || patchConnector === null || refreshRequired
                }
                onChange={(event) =>
                  setDraftField("provider", event.currentTarget.value)
                }
              />
            )}
          </FormField>
          <FormField
            label="Mode"
            hint="The server accepts only a code-registry provider/mode pair."
            required
          >
            {(controlProps) => (
              <TextInput
                {...controlProps}
                value={draft.mode}
                pattern="[a-z][a-z0-9._+\-]{0,127}"
                maxLength={128}
                autoComplete="off"
                disabled={
                  pending !== null || patchConnector === null || refreshRequired
                }
                onChange={(event) =>
                  setDraftField("mode", event.currentTarget.value)
                }
              />
            )}
          </FormField>
          <FormField
            label="Safe identifier alias"
            hint="Letters, numbers, dots, underscores, and hyphens only."
          >
            {(controlProps) => (
              <TextInput
                {...controlProps}
                value={draft.safeIdentifierAlias}
                pattern="[A-Za-z0-9][A-Za-z0-9._\-]{0,127}"
                maxLength={128}
                autoComplete="off"
                disabled={
                  pending !== null || patchConnector === null || refreshRequired
                }
                onChange={(event) =>
                  setDraftField(
                    "safeIdentifierAlias",
                    event.currentTarget.value,
                  )
                }
              />
            )}
          </FormField>
          <div className="admin-settings__readonly-field">
            <span>Environment</span>
            <strong>{detail.environment}</strong>
            <small>
              Derived from the trusted server runtime; never submitted.
            </small>
          </div>
          <div className="admin-settings__readonly-field">
            <span>Responsible owner</span>
            <strong>{connector.owner ?? "Not assigned"}</strong>
            <small>
              Read-only until an organization-scoped membership reference is
              available.
            </small>
          </div>
          <div className="admin-settings__readonly-field">
            <span>Operational contact</span>
            <strong>{connector.operationalContact ?? "Not assigned"}</strong>
            <small>
              Read-only until an organization-scoped contact reference is
              available.
            </small>
          </div>
          <div className="admin-settings__readonly-field admin-settings__readonly-field--wide">
            <span>Callback route</span>
            <strong>
              {connector.callbackUrl ?? "Server-derived / not configured"}
            </strong>
            <small>
              Approved callback routes are enforced server-side and are never
              supplied as arbitrary browser input.
            </small>
          </div>
        </div>
        <FormActions align="start">
          <button
            type="submit"
            disabled={
              pending !== null || patchConnector === null || refreshRequired
            }
          >
            {pending === "metadata"
              ? "Saving safe metadata…"
              : "Save safe metadata"}
          </button>
        </FormActions>
      </form>

      <div className="admin-settings__rotation">
        <h4>Request credential rotation</h4>
        <p>
          A request records the declared secret name and reason only. Actual
          rotation occurs outside the browser. Recording the request requires
          recent server-verified multi-factor authentication.
        </p>
        {requestRotation === null && (
          <p className="admin-settings__unavailable">
            {canRequestRotation
              ? "Rotation-request transport is not configured."
              : "This account does not have permission to request credential rotation."}
          </p>
        )}
        {eligibleRotationSecrets.length === 0 ? (
          <p>
            {connector.requiredSecrets.length === 0
              ? "This connector has no declared secret names in its current mode."
              : "No declared secret reference is currently eligible for rotation. A reference must be verified or referenced first."}
          </p>
        ) : connector.configurationVersion === null ? (
          <p>
            Create the first safe configuration version before requesting
            rotation.
          </p>
        ) : (
          <form onSubmit={submitRotation}>
            <div className="admin-settings__form-grid">
              <FormField label="Declared secret name" required>
                {(controlProps) => (
                  <Select
                    {...controlProps}
                    value={secretName}
                    disabled={
                      pending !== null ||
                      requestRotation === null ||
                      refreshRequired
                    }
                    onChange={(event) =>
                      setSecretName(event.currentTarget.value)
                    }
                  >
                    {eligibleRotationSecrets.map((secret) => (
                      <option key={secret.name} value={secret.name}>
                        {secret.name} — {secret.status}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
              <FormField label="Rotation reason" required>
                {(controlProps) => (
                  <Select
                    {...controlProps}
                    value={rotationReason}
                    disabled={
                      pending !== null ||
                      requestRotation === null ||
                      refreshRequired
                    }
                    onChange={(event) =>
                      setRotationReason(
                        event.currentTarget.value as ConnectorRotationReason,
                      )
                    }
                  >
                    {connectorRotationReasons.map((reason) => (
                      <option key={reason} value={reason}>
                        {rotationReasonLabels[reason]}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
            </div>
            <FormActions align="start">
              <button
                type="submit"
                disabled={
                  pending !== null ||
                  requestRotation === null ||
                  refreshRequired
                }
              >
                {pending === "rotation"
                  ? "Recording rotation request…"
                  : "Request rotation"}
              </button>
            </FormActions>
          </form>
        )}
      </div>
    </section>
  );
}
